import { ChevronDown, ChevronRight } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Badge from "src/components/common/Badge";
import Button from "src/components/app/Button";
import Header from "src/components/app/layout/Header";
import Layout from "src/components/app/layout/Layout";
import { CycleItemType } from "src/types/journey.types";
import { COMPLETED_CYCLE_VISIT_STATUS } from "src/constants/journey";
import moment from "moment";
import { DATE_FORMAT_STRING } from "src/constants/dataTimeFormats";
import ClaimFreeDrug from "src/components/app/progress/journey/ClaimFreeDrug";
import { EVENT_NAMES, SCREEN_NAMES } from "src/helpers/analytics/events";
import analytics from "src/helpers/analytics";
import supportPrograms from "src/data/app/programs";

const JourneyDetailsScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"];
  const navigate = useNavigate();
  const location = useLocation();
  const { cycles, nextCycle, vivitraCardType } = location.state || {};
  const [expandedIndex, setExpandedIndex] = useState<string | null>(null);
  const hasInitializedRef = useRef(false);
  const config =
    supportPrograms[supportProgramKey as keyof typeof supportPrograms];
  const isDrugClaim = config?.config?.enableDrugClaimJourney;

  useEffect(() => {
    // Event: TREATMENT_JOURNEY_SCREEN_VIEWED
    analytics.pageView(SCREEN_NAMES.TREATMENT_JOURNEY_SCREEN);
  }, []);

  useEffect(() => {
    // Open the first card by default only once
    if (cycles?.data?.length > 0 && !hasInitializedRef.current) {
      const firstCycle = cycles?.data?.[0];
      if (firstCycle?.patient_visit_id) {
        setExpandedIndex(firstCycle?.patient_visit_id);
        hasInitializedRef.current = true;
      }
    }
  }, [cycles]);

  const handleExpandCycle = (cycle: CycleItemType) => {
    if (hasMultipleGroups) {
      setExpandedIndex((prev) =>
        prev === cycle?.patient_visit_id ? null : cycle?.patient_visit_id,
      );
    }
  };

  // const isCycleCompleted = (status: string) => {
  //   return COMPLETED_CYCLE_VISIT_STATUS.includes(status);
  // };

  // GROUP CYCLES BY DRUG
  // const groupedCycles: { primary_drug: string; cycles: CycleItemType[] }[] = [];
  // cycles?.data?.forEach((cycle: CycleItemType) => {
  //   const primary_drug =
  //     cycle.visit_meta_info?.primary_drugs?.[0]?.drug_label || "Unknown";
  //   const lastGroup = groupedCycles[groupedCycles.length - 1];

  //   if (!lastGroup || lastGroup.primary_drug !== primary_drug) {
  //     // New drug group
  //     groupedCycles.push({ primary_drug, cycles: [cycle] });
  //   } else {
  //     // Same drug as previous → add to same group
  //     lastGroup.cycles.push(cycle);
  //   }
  // });

  const groupedCycles = useMemo(() => {
    const groups: { primary_drug: string; cycles: CycleItemType[] }[] = [];
    cycles?.data?.forEach((cycle: CycleItemType) => {
      const primary_drug =
        cycle.visit_meta_info?.primary_drugs?.[0]?.drug_label || "Unknown";
      const lastGroup = groups?.[groups?.length - 1];

      if (!lastGroup || lastGroup?.primary_drug !== primary_drug) {
        // New drug group
        groups?.push({ primary_drug, cycles: [cycle] });
      } else {
        // Same drug as previous → add to same group
        lastGroup?.cycles?.push(cycle);
      }
    });
    return groups ?? [];
  }, [cycles?.data]);

  // // STATUS FOR GROUP OF CYCLES
  // const getGroupStatus = (
  //   cycles: CycleItemType[]
  // ): "current" | "upcoming" | "previous" => {
  //   const hasCompleted = cycles.some((cycle) =>
  //     isCycleCompleted(cycle.visit_status)
  //   );
  //   const allCompleted = cycles.every((cycle) =>
  //     isCycleCompleted(cycle.visit_status)
  //   );
  //   const hasUpcoming = cycles.some((cycle) => cycle.visit_status === "N");

  //   if (allCompleted) {
  //     return "previous";
  //   } else if (hasUpcoming || hasCompleted) {
  //     return "current";
  //   }
  //   return "upcoming";
  // };

  const groupsCount = groupedCycles?.length ?? 0;
  const hasSingleGroupWithSingleCycle =
    groupsCount === 1 && groupedCycles?.[0]?.cycles?.length === 1;
  const hasMultipleGroups = groupsCount > 1;

  const renderGroupedCycles = () => {
    // No groups found
    if (groupsCount === 0) {
      return (
        <div className="w-full flex items-center justify-center">
          <p className="text-sm text-gray-500">No cycles found</p>
        </div>
      );
    }
    // Single group and single cycle
    if (hasSingleGroupWithSingleCycle) {
      return (
        <div>
          <CycleCard
            key={groupedCycles?.[0]?.cycles?.[0]?.patient_visit_id}
            cycle={groupedCycles?.[0]?.cycles?.[0]}
          />
        </div>
      );
    }
    // Multiple groups and multiple cycles
    return groupedCycles?.map((group, groupIndex) => (
      <div
        key={group?.primary_drug + groupIndex}
        className="relative w-full bg-white rounded-lg border border-gray-200 overflow-hidden"
      >
        <div
          className="flex items-center justify-between gap-2 p-3"
          onClick={() => handleExpandCycle(group?.cycles?.[0])}
        >
          <div className="space-y-1">
            <h5 className="text-sm font-bold capitalize">
              {group?.primary_drug}
            </h5>
            <div className="text-xs text-gray-500">
              Cycles {group?.cycles?.[group?.cycles?.length - 1]?.visit_cycle} -{" "}
              {group?.cycles?.[0]?.visit_cycle}
              {" • "}
              {group?.cycles?.[0]?.visit_meta_info?.therapy_type}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* <Status type={getGroupStatus(group.cycles)}>
        {getGroupStatus(group.cycles)}
      </Status> */}
            <Status type={groupIndex === 0 ? "current" : "previous"}>
              {groupIndex === 0 ? "current" : "previous"}
            </Status>

            {/* <div className="w-5 min-w-5 h-5 flex items-center justify-center text-gray-500">
              {expandedIndex ===
              group?.cycles?.[0]?.patient_visit_id ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </div> */}
            <GroupedCyclesExpandIndicator
              showIndicator={hasMultipleGroups}
              expanded={expandedIndex === group?.cycles?.[0]?.patient_visit_id}
            />
          </div>
        </div>

        {expandedIndex === group?.cycles?.[0]?.patient_visit_id && (
          <div className="relative w-full">
            <div className="px-3 py-2 border-t border-gray-200 flex flex-col gap-y-2">
              {group?.cycles?.map((cycle) => (
                <CycleCard key={cycle?.patient_visit_id} cycle={cycle} />
              ))}
            </div>
          </div>
        )}
      </div>
    ));
  };

  return (
    <Layout supportProgramKey={supportProgramKey || ""}>
      <div className="relative pt-[52px] h-full">
        <div className="fixed max-w-lg mx-auto top-0 left-0 right-0 z-10">
          <Header
            title="Treatment Journey"
            titlePosition="center"
            showBackButton={true}
            onBackClick={() => {
              navigate(-1);
            }}
          />
        </div>
        <div className="w-full max-w-lg h-full">
          <div className="relative w-full px-4 py-4">
            <div className="flex flex-col gap-4">
              {nextCycle?.visit_meta_info?.is_free_drugs_available === "1" &&
                (nextCycle?.visit_status === "N" || nextCycle !== undefined) &&
                isDrugClaim && (
                  <ClaimFreeDrug
                    nextCycle={nextCycle}
                    vivitraCardType={vivitraCardType}
                  />
                )}

              {/* Drug changed alert - shows when treatment drug changes between cycles */}
              {cycles?.data?.length >= 2 &&
                cycles?.data?.[0]?.visit_meta_info?.primary_drugs?.[0]
                  ?.drug_name !==
                  cycles?.data?.[1]?.visit_meta_info?.primary_drugs?.[0]
                    ?.drug_name && (
                  <DrugChangedAlert
                    from={
                      cycles?.data?.[1]?.visit_meta_info?.primary_drugs?.[0]
                        ?.drug_label // previous cycle drug
                    }
                    to={
                      cycles?.data?.[0]?.visit_meta_info?.primary_drugs?.[0]
                        ?.drug_label // current cycle drug
                    }
                  />
                )}

              <div className="w-full flex flex-col gap-y-2">
                {/* Render grouped cycles */}
                {renderGroupedCycles()}
                {/* <div className="flex flex-col gap-y-2">
              {cycles?.data?.map((cycle: CycleItemType, index: number) => (
                <CycleCard key={index.toString()} cycle={cycle} />
              ))}
            </div> */}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

const GroupedCyclesExpandIndicator = React.memo(
  ({
    showIndicator,
    expanded,
  }: {
    showIndicator: boolean;
    expanded: boolean;
  }) => {
    if (!showIndicator) return null;

    return (
      <div className="w-5 min-w-5 h-5 flex items-center justify-center text-gray-500">
        {expanded ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
      </div>
    );
  },
);

const DrugChangedAlert = ({ from, to }: { from: string; to: string }) => {
  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5">
      <div className="flex items-center gap-2">
        <div className="w-1.5 h-1.5 bg-blue-500 rounded-full flex-shrink-0"></div>
        <p className="text-xs text-gray-700 font-medium">
          Treatment drug changed from{" "}
          <span className="font-bold capitalize text-gray-900">{from}</span> to{" "}
          <span className="font-bold capitalize text-gray-900">{to}</span> for
          enhanced therapy effectiveness
        </p>
      </div>
    </div>
  );
};

const Status = ({
  type,
  children,
}: {
  type: "current" | "upcoming" | "previous";
  children: React.ReactNode;
}) => {
  switch (type) {
    case "current":
      return (
        <div className="text-xs text-green-700 font-medium bg-green-100 border border-green-200 px-2.5 py-0.5 rounded-full capitalize">
          {children}
        </div>
      );
    case "upcoming":
      return (
        <div className="text-xs text-gray-500 font-medium bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded-full capitalize">
          {children}
        </div>
      );
    case "previous":
      return (
        <div className="text-xs text-gray-500 font-medium bg-gray-100 border border-gray-200 px-2.5 py-0.5 rounded-full capitalize">
          {children}
        </div>
      );
  }
};

const CycleCard = ({ cycle }: { cycle: CycleItemType }) => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];

  const isCycleCompleted = (status: string) => {
    return COMPLETED_CYCLE_VISIT_STATUS.includes(status);
  };

  const handleViewDetails = () => {
    // Event: VIEW_CYCLE_DETAILS_CLICKED
    analytics.trackEvent(EVENT_NAMES.VIEW_CYCLE_DETAILS_CLICKED, {
      screen_name: SCREEN_NAMES.CYCLE_DETAILS_SCREEN,
      support_program_key: supportProgramKey,
      ...cycle,
    });

    // Navigate to cycle details screen
    navigate(`/app/${supportProgramKey}/journey-details/cycle-details`, {
      state: {
        cycle,
      },
    });
  };

  return (
    <div className="w-full p-2 border border-gray-200 bg-gray-50 rounded-lg">
      {/* bg-gray-50 */}
      <div className="w-full flex items-start justify-between gap-2">
        <div className="flex flex-col gap-2">
          <h6 className="text-sm font-medium">Cycle {cycle.visit_cycle}</h6>
          <p className="text-xs text-gray-500">
            Completed:{" "}
            {isCycleCompleted(cycle.visit_status)
              ? moment(cycle.rescheduled_date).format(DATE_FORMAT_STRING)
              : "N/A"}
          </p>
          <div className="relative">
            <Button
              size="sm"
              variant="outlined"
              rounded="full"
              onClick={handleViewDetails}
            >
              View Details
            </Button>
          </div>
        </div>
        <Badge
          type={isCycleCompleted(cycle.visit_status) ? "success" : "warning"}
        >
          {isCycleCompleted(cycle.visit_status) ? "Completed" : "Upcoming"}
        </Badge>
      </div>
    </div>
  );
};

export default JourneyDetailsScreen;
